import { HttpClient } from '@angular/common/http';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormControlStatus, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { environment } from 'apps/mba-cpr-survey-portal/src/environments/environment';
import { combineLatest, distinctUntilChanged, lastValueFrom, Subject, takeUntil } from 'rxjs';

@Component({
  standalone: true,
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    ReactiveFormsModule,
  ],
  selector: 'app-tow-id-form-control',
  template: `
    <div style="display: flex; gap: var(--global-padding)">
      <mat-form-field style="width: 100%">
        <mat-label>Route</mat-label>
        <input
          matInput
          maxlength="4"
          pattern="^[A-Z]-$|^[A-Z]{2,3}$"
          required
          [formControl]="routeCode"
        />
      </mat-form-field>

      <mat-form-field style="width: 100%">
        <mat-label>Tow Number</mat-label>
        <input
          matInput
          type="number"
          min="1"
          max="999"
          step="0"
          required
          [formControl]="routeNumber"
        />
        @if (routeNumber.enabled) {
          <button
            mat-icon-button
            matIconSuffix
            (click)="$event.stopPropagation(); autoIncrement()"
            matTooltip="Auto-increment"
            [disabled]="routeCode.invalid"
          >
            <mat-icon>bolt</mat-icon>
          </button>
        }
        @if (routeNumber.enabled) {
          <mat-hint
            >Click on the button to the right to autofill this field with the next incremental
            number for the route.</mat-hint
          >
        }
      </mat-form-field>
    </div>
  `,
})
export class TowIdFormControlComponent implements OnInit, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  private readonlyState = false;

  readonly routeCode = new FormControl<string>('');
  readonly routeNumber = new FormControl<number>(null);

  // The parent-provided form control to observe and update
  @Input() towId: FormControl<string>;

  @Input() set readonly(value: boolean) {
    this.readonlyState = value;
    this.syncRouteControlsState(this.getCurrentStatus());
  }

  private readonly http = inject(HttpClient);

  ngOnInit() {
    this.syncRouteControlsState(this.getCurrentStatus());

    // Reactively combine on change to create the full towId value
    combineLatest([this.routeCode.valueChanges, this.routeNumber.valueChanges])
      .pipe(distinctUntilChanged(), takeUntil(this.unsubscribe$))
      .subscribe(([routeCode, routeNumber]) => {
        this.towId.setValue((routeNumber ?? '') + routeCode, { emitEvent: false });
        this.towId.markAsDirty();
        this.towId.markAsTouched();
      });

    // Copy initial status and bind future status changes
    this.towId.statusChanges
      .pipe(distinctUntilChanged(), takeUntil(this.unsubscribe$))
      .subscribe((status: FormControlStatus) => this.syncRouteControlsState(status));

    // Copy initial value and bind future value changes
    this.towId.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value: string) => {
      const result = /^([0-9]{1,3})([A-Z]-$|[A-Z]{2,3}$)/.exec(value);
      this.routeCode.setValue((result && result[2]) || '');
      const routeNumber = result && result[1] && Number.parseInt(result[1]);
      this.routeNumber.setValue((!Number.isNaN(routeNumber) && routeNumber) || null);
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private syncRouteControlsState(status: FormControlStatus) {
    if (status === 'DISABLED' || this.readonlyState) {
      this.routeCode.disable({ emitEvent: false });
      this.routeNumber.disable({ emitEvent: false });
    } else {
      this.routeCode.enable({ emitEvent: false });
      this.routeNumber.enable({ emitEvent: false });
    }
  }

  private getCurrentStatus(): FormControlStatus {
    return this.towId?.disabled ? 'DISABLED' : this.towId?.status;
  }

  async autoIncrement() {
    this.routeNumber.setValue(
      await lastValueFrom(
        this.http.get<number>(
          `${environment.cprServiceApi}recording-event/${this.routeCode.value}/next`,
        ),
      ),
    );
  }
}
